// src/components/opportunity/OpportunityProposalContactPanel.tsx

import { useEffect, useMemo, useState } from "react";
import type { Opportunity } from "@/types/opportunity";
import { useAuth } from "@/contexts/AuthContext";
import { useOrgSettings } from "@/hooks/useOrgSettings";
import { fetchRepContactLocation } from "@/lib/opportunity/fetchRepContactLocation";
import type { RepContactLocation } from "@/lib/opportunity/fetchRepContactLocation";
import { resolveManagerPresetFormatKey } from "@/lib/opportunity/formatRegistry";
import { updateOpportunityContactPrefs } from "@/lib/opportunity/opportunityClient";
import {
  resolveFormatProposalContactSettings,
  resolveProposalContact,
} from "@/lib/opportunity/proposalContactResolver";
import { formatProposalContactLines } from "@/lib/opportunity/proposalContactTypes";

export type OpportunityProposalContactPanelProps = {
  opportunity: Opportunity;
  onChanged: () => void;
};

export function OpportunityProposalContactPanel(props: OpportunityProposalContactPanelProps) {
  const { opportunity, onChanged } = props;
  const auth = useAuth();
  const org = useOrgSettings();
  const [allowRep, setAllowRep] = useState(false);
  const [formatOverride, setFormatOverride] = useState<Awaited<
    ReturnType<typeof resolveFormatProposalContactSettings>
  > | null>(null);
  const [activeLocation, setActiveLocation] = useState<RepContactLocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const presetFormatKey = resolveManagerPresetFormatKey(opportunity.product_type);
  const useRepContact = opportunity.use_rep_proposal_contact === true;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const locId = auth.profile?.active_location_id;
    void Promise.all([
      resolveFormatProposalContactSettings({
        formatKey: presetFormatKey,
        productType: opportunity.product_type,
      }),
      locId ? fetchRepContactLocation(locId) : Promise.resolve(null),
    ]).then(([formatSettings, loc]) => {
      if (cancelled) return;
      setFormatOverride(formatSettings);
      setAllowRep(formatSettings.allowRepContactOverride);
      setActiveLocation(loc);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [auth.profile?.active_location_id, opportunity.product_type, presetFormatKey]);

  useEffect(() => {
    if (!allowRep || useRepContact || loading) return;
    let cancelled = false;
    setBusy(true);
    void updateOpportunityContactPrefs({
      opportunity_id: opportunity.id,
      use_rep_proposal_contact: true,
    }).then((r) => {
      if (cancelled) return;
      setBusy(false);
      if (r.ok) onChanged();
    });
    return () => {
      cancelled = true;
    };
  }, [allowRep, loading, onChanged, opportunity.id, useRepContact]);

  const previewLines = useMemo(() => {
    const resolved = resolveProposalContact({
      orgRow: org.proposalContactRow,
      brandNameFallback: org.brandName,
      organizationNameFallback: org.organizationName,
      formatOverride: formatOverride?.formatContact,
      useRepContact,
      allowRepContactOverride: allowRep,
      profile: auth.profile,
      location: activeLocation,
    });
    return formatProposalContactLines(resolved.contact);
  }, [
    activeLocation,
    allowRep,
    auth.profile,
    formatOverride?.formatContact,
    org.brandName,
    org.organizationName,
    org.proposalContactRow,
    useRepContact,
  ]);

  async function toggleUseRep(next: boolean) {
    setBusy(true);
    setError(null);
    const r = await updateOpportunityContactPrefs({
      opportunity_id: opportunity.id,
      use_rep_proposal_contact: next,
    });
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    onChanged();
  }

  if (loading) return null;

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-900 space-y-2">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
          Proposal contact
        </h4>
        <p className="text-[11px] text-gray-500 dark:text-zinc-400 mt-0.5">
          Contact block printed on the cover letter and signature page.
        </p>
      </div>

      <div className="text-xs text-gray-700 dark:text-zinc-300 whitespace-pre-line rounded-lg bg-gray-50 dark:bg-zinc-800/60 px-3 py-2">
        {previewLines.join("\n")}
      </div>

      {allowRep ? (
        <label className="flex items-start gap-2 text-xs text-gray-700 dark:text-zinc-300">
          <input
            type="checkbox"
            checked={useRepContact}
            disabled={busy}
            onChange={(e) => void toggleUseRep(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            Use my contact details (name, location address, location phone, and email) on this
            proposal.
          </span>
        </label>
      ) : null}

      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </section>
  );
}
